import * as actions from "appRedux/actions/setup/systemSetup/systemConfigActionType"
import { systemParamsDefault, systemParamsType } from "./systemConfigType"

// ---- Types ----
type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export type PrepareUpdate = DeepPartial<systemParamsType>;

// รับแพตช์ได้ทีละ "คีย์บนสุด" เท่านั้น (เช่น { SYSTEM: {...} })
type OneKeyPatch = {
    [K in keyof systemParamsType]: (
        Record<K, DeepPartial<systemParamsType[K]>> &
        Partial<Record<Exclude<keyof systemParamsType, K>, never>>
    )
}[keyof systemParamsType];

// ---- Utils ----
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * deepMerge:
 * - ถ้าทั้ง base และ patch เป็น object: merge ลงไปทีละคีย์
 * - ถ้าคีย์ยังไม่เคยมีใน base -> "เพิ่ม"
 * - ถ้าคีย์มีอยู่แล้ว -> ถ้าทั้งคู่เป็น object จะ merge ต่อ, ไม่งั้น "replace"
 * - ถ้าเป็น array/primitive -> "replace"
 */
function deepMerge<T>(
    base: DeepPartial<T> | undefined,
    patch: DeepPartial<T>
): DeepPartial<T> {
    if (!isPlainObject(base) || !isPlainObject(patch)) {
        // base ไม่มี หรืออย่างน้อยหนึ่งฝ่ายไม่ใช่ plain object -> ใช้ patch ทับเลย
        return patch;
    }

    const out: Record<string, unknown> = { ...base };
    for (const key of Object.keys(patch)) {
        const pv = (patch as any)[key];
        const bv = (base as any)[key];

        if (isPlainObject(bv) && isPlainObject(pv)) {
            out[key] = deepMerge(bv, pv); // merge ลึก
        } else {
            out[key] = pv; // replace
        }
    }
    return out as DeepPartial<T>;
}

// ---- Main API ----
/**
 * pushUpdate:
 * - รับ section ทีละก้อน เช่น { SYSTEM: { ALLOW_OVERBOOK: true } }
 * - ถ้าเป็นคีย์บนสุดเดิม: จะ "merge" เข้าไป (คีย์ใหม่เพิ่ม, คีย์ซ้ำ replace)
 * - คืนค่าเป็นอ็อบเจ็กต์ใหม่ (immutable)
 */
export function pushUpdate(
    prev: Readonly<PrepareUpdate> | undefined,
    section: OneKeyPatch
): PrepareUpdate {
    const keys = Object.keys(section) as (keyof systemParamsType)[];
    if (keys.length !== 1) {
        throw new Error("โปรดส่งอ็อบเจ็กต์ที่มี top-level key เดียว เช่น { SYSTEM: {...} }");
    }
    const k = keys[0];

    const mergedSection = deepMerge<systemParamsType[typeof k]>(
        (prev?.[k] as DeepPartial<systemParamsType[typeof k]>) ?? undefined,
        section[k] as DeepPartial<systemParamsType[typeof k]>
    );

    return {
        ...(prev ?? {}),
        [k]: mergedSection,
    } as PrepareUpdate;
}


export type initialState = {
    loader: boolean,
    dataSource: systemParamsType,
    systemPrepareUpdate?: PrepareUpdate
    process: { status: false, action: "", message: "" }
}

const INIT_STATE: initialState = {
    loader: false,
    dataSource: systemParamsDefault,
    systemPrepareUpdate: undefined,
    process: { status: false, action: "", message: "" }
}


export default (state: initialState = INIT_STATE, action: actions.SystemConfigAction): initialState => {
    switch (action.type) {
        case actions.SYSTEM_CONFIG_PREVIEW: {
            return {
                ...state,
                loader: true,
                process: { status: false, action: "", message: "" }
            }
        }
        case actions.SYSTEM_CONFIG_UPDATE: {
            return {
                ...state,
                loader: true,
                process: { status: false, action: "", message: "" }
            }
        }
        case actions.SYSTEM_CONFIG_DATASOURCE_SUCCESS: {
            return {
                ...state,
                loader:false,
                dataSource: action.payload
            }
        }
        case actions.SYSTEM_CONFIG_PROCESS_SUCCESS: {
            return {
                ...state,
                loader: false,
                process: { ...action.payload }
            }
        }
        case actions.SYSTEM_CONFIG_PREPARE_UPDATE: {
            const value: OneKeyPatch = action.payload
            const newPrepareData = pushUpdate(state.systemPrepareUpdate, value)

            return {
                ...state,
                systemPrepareUpdate: newPrepareData
            }
        }

        case actions.SYSTEM_CONFIG_PROCESS_RESET: {
            return {
                ...state,
                loader: false,
                process: { status: false, action: "", message: "" }
            }
        }
        case actions.SYSTEM_CONFIG_PREPARE_UPDATE_RESET:{
            return{
                ...state,
                loader:false,
                systemPrepareUpdate:undefined
            }
        }
        case actions.SYSTEM_CONFIG_RESET: {
            return {
                loader: false,
                dataSource: state.dataSource,
                systemPrepareUpdate: undefined,
                process: { status: false, action: "", message: "" }
            }
        }
        default:
            return state
    }
}